import { Box, Button, Stack, Typography } from '@mui/material';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';

import ContentLoader from '@components/ContentLoader';
import NullStateDiv from '@components/NullStateDiv';
import VitalSignCard from '@components/VitalSignCard';

import { DEVICES } from '@constants/routes';

import type { ObservationRecord } from '@models/observation';

import { sortDates } from '@utils/sort/date';

import { FontWeight } from '../../../theme/typography';
import AddRecord from '../AddRecord';

const messages = defineMessages({
  connect: {
    id: 'dashboard.connect',
    defaultMessage: 'Connect Health Device',
  },
});

export default function ObservationTracker({
  loading,
  observations,
  title,
  route,
  noData,
  viewAll,
}: {
  title: string;
  route: string;
  noData: React.ReactNode;
  viewAll: string;
  loading: boolean;
  observations: Record<string, ObservationRecord | null>;
}) {
  const { formatMessage } = useIntl();
  const records = Object.fromEntries(
    Object.entries(observations)
      .filter(([, observation]) => observation)
      .sort(([, a], [, b]) =>
        sortDates('desc')(
          { data: a?.getObservationDate() ?? null },
          { data: b?.getObservationDate() ?? null }
        )
      )
      .filter((_, index) => index < 3)
  );

  return (
    <Box sx={{ my: 1 }}>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent={'space-between'}
        pr={1}
        pl={2}
      >
        <Typography
          variant="overline"
          sx={{ fontSize: 14, fontWeight: FontWeight.Regular, lineHeight: 0.5 }}
        >
          {title}
        </Typography>
        <Button variant="text" onClick={() => navigate(route)}>
          <Typography
            color="info.main"
            variant="overline"
            sx={{
              fontSize: 14,
              fontWeight: FontWeight.Regular,
              lineHeight: 0.5,
            }}
          >
            {viewAll}
          </Typography>
        </Button>
      </Stack>
      <ContentLoader loading={loading}>
        {/* Auto-fit rather than fixed breakpoint spans: the card decides its
        own layout from the column it lands in, so the grid only has to keep
        columns above the width the card reads as cramped. */}
        <Box
          display="grid"
          gap={1}
          gridTemplateColumns="repeat(auto-fit, minmax(320px, 1fr))"
        >
          {Object.entries(records).map(([code, observation]) => {
            const type = code.replace(/_/g, '-');
            return (
              <VitalSignCard
                code={type}
                key={`latest-${type}`}
                loading={loading}
                onClick={() => {
                  navigate(`${route}${type}`);
                }}
                record={observation ?? undefined}
              />
            );
          })}
        </Box>

        {Object.values(records).every((record) => !record) ? (
          <NullStateDiv>
            {noData}
            <AddRecord route={DEVICES} text={formatMessage(messages.connect)} />
          </NullStateDiv>
        ) : null}
      </ContentLoader>
    </Box>
  );
}
